import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Label,
    FormGroup,
    Form,
} from "reactstrap";
import Select from "react-select";
import {
    AsyncClientSelect,
    AsyncCandidateSelect,
    AsyncSourceSelect,
    AsyncUserSelect,
} from "components/Common/AsyncSearchSelects";
import { useCommonFilterOptions, resolveMetadataDropdownApiKey } from "libs/context/CommonFilterOptionsContext";
import {
    ASYNC_AUTOCOMPLETE_API_TO_KIND,
    getAsyncFilterLabelFieldKey,
    isMetadataAsyncSelectFilterDefinition,
} from "helpers/metadataFilterHelpers";
import ManageFilterToolbarPanel from "components/Common/ManageFilterToolbarPanel";
import { useFilterPreferences } from "libs/context/FilterPreferencesContext";

const selectZIndexStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const getSelectedOption = (options = [], value) => {
    const list = Array.isArray(options) ? options : [];
    return (
        list.find((opt) => String(opt?.value ?? "") === String(value ?? "")) || null
    );
};

const resolveAsyncSelectKind = (item = {}) => {
    if (item?.asyncKind) return item.asyncKind;
    return ASYNC_AUTOCOMPLETE_API_TO_KIND[item?.api] || null;
};

const isFilterFieldActive = (item = {}, formData = {}) => {
    const fieldName = item?.field;
    if (!fieldName) return false;
    const value = formData[fieldName];
    if (item?.type === "toggle") {
        return Boolean(value);
    }
    if (item?.type === "multiselect") {
        return Array.isArray(value) && value.length > 0;
    }
    return value !== null && value !== undefined && String(value).trim() !== "";
};

const activeFieldStyle = {
    borderRadius: "10px",
    outline: "2px solid #556ee6",
};

const AllCommonFilterModal = ({
    setCommonFilterModalOpen,
    commonFilterModalOpen,
    modalTitle,
    handleSubmit,
    filterData = [],
    filterItemData = [],
    commonResetFilter = false,
    setCommonResetFilter,
    pageName = "",
    metadataFilterDefinitions = [],
    enableFilterToolbarCustomization = false,
}) => {
    const { getDropdownOptionsByApiKey } = useCommonFilterOptions();
    const resolvedPageName = String(pageName ?? "").trim();
    const shouldCustomizeToolbar =
        enableFilterToolbarCustomization &&
        resolvedPageName &&
        Array.isArray(metadataFilterDefinitions) &&
        metadataFilterDefinitions.length > 0;
    const {
        toolbarFieldOrder,
        advancedFieldOrder,
        handleFilterDragEnd,
        resetFilterPreferences,
    } = useFilterPreferences(
        shouldCustomizeToolbar ? resolvedPageName : "",
        metadataFilterDefinitions
    );
    const filterConfig = useMemo(
        () => (Array.isArray(filterData) && filterData.length ? filterData : filterItemData || []),
        [filterData, filterItemData]
    );
    const [formData, setFormData] = useState({});
    const [dropdownOptionsByField, setDropdownOptionsByField] = useState({});

    const buildInitialFormData = () => {
        const obj = {};
        filterConfig.forEach((f) => {
            const fieldName = f?.field;
            if (!fieldName) return;
            if (f?.type === "toggle") {
                obj[fieldName] = typeof f?.value === "boolean" ? f.value : false;
            } else if (f?.type === "multiselect") {
                obj[fieldName] = Array.isArray(f?.value) ? f.value : [];
            } else {
                obj[fieldName] = f?.value ?? null;
            }
            if (isMetadataAsyncSelectFilterDefinition(f)) {
                const labelFieldKey = getAsyncFilterLabelFieldKey(fieldName);
                obj[labelFieldKey] = f?.initialOption?.label ?? "";
            }
        });
        return obj;
    };

    useEffect(() => {
        if (!commonFilterModalOpen) return;
        setFormData(buildInitialFormData());
    }, [commonFilterModalOpen, filterConfig]);

    const handleAsyncSelectChange = (fieldName, selectedOption, item) => {
        const nextValue = selectedOption?.value ?? "";
        const labelFieldKey = getAsyncFilterLabelFieldKey(fieldName);
        setFormData((prev) => ({
            ...prev,
            [fieldName]: nextValue,
            [labelFieldKey]: selectedOption?.label ?? "",
        }));
        if (typeof item?.onSelectChange === "function") {
            item.onSelectChange(selectedOption, setFormData, fieldName);
        }
    };

    useEffect(() => {
        const dropdownItems = filterConfig.filter(
            (item) =>
                (item?.type === "dropdown" || item?.type === "multiselect") &&
                item?.field
        );
        if (!dropdownItems.length) {
            setDropdownOptionsByField({});
            return;
        }

        let cancelled = false;
        (async () => {
            const results = await Promise.all(
                dropdownItems.map(async (item) => {
                    const staticOptions = Array.isArray(item?.itemArray) ? item.itemArray : [];
                    if (staticOptions.length) return [item.field, staticOptions];
                    const fetchedOptions = await getDropdownOptionsByApiKey(
                        resolveMetadataDropdownApiKey(item),
                        { pageName: resolvedPageName }
                    );
                    return [item.field, fetchedOptions];
                })
            );
            if (cancelled) return;
            const next = {};
            results.forEach(([field, options]) => {
                next[field] = options;
            });
            setDropdownOptionsByField(next);
        })();

        return () => {
            cancelled = true;
        };
    }, [filterConfig, getDropdownOptionsByApiKey, resolvedPageName]);

    useEffect(() => {
        if (!commonResetFilter) return;
        const resetObj = {};
        filterConfig.forEach((f) => {
            const fieldName = f?.field;
            if (!fieldName) return;
            if (f?.type === "toggle") {
                resetObj[fieldName] = false;
            } else if (f?.type === "multiselect") {
                resetObj[fieldName] = [];
            } else {
                resetObj[fieldName] = "";
            }
            if (isMetadataAsyncSelectFilterDefinition(f)) {
                resetObj[getAsyncFilterLabelFieldKey(fieldName)] = "";
            }
        });
        setFormData(resetObj);
        if (typeof setCommonResetFilter === "function") {
            setCommonResetFilter(false);
        }
    }, [commonResetFilter, filterConfig, setCommonResetFilter]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" || type === "switch" ? checked : value,
        });
    };

    const closeModal = () => setCommonFilterModalOpen(false);

    const onSubmit = (e) => {
        e.preventDefault();
        handleSubmit?.(formData);
    };

    return (
        <Modal
            isOpen={commonFilterModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-dialog-right modal-full-height"
        >
            <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">{modalTitle}</h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                <Form onSubmit={onSubmit}>
                    {filterConfig.map((item, index) => {
                        const fieldName = item?.field;
                        if (!fieldName) return null;

                        const isMonthInput =
                            item.type === "month" ||
                            (item.type === "date" &&
                                String(fieldName).toLowerCase() === "hiremonth");

                        const isActive = isFilterFieldActive(item, formData);

                        return (
                            <FormGroup key={index}>
                                <Label>{item.title}</Label>

                                <div style={isActive ? activeFieldStyle : undefined}>
                                {item.type === "text" && (
                                    <Input
                                        type="text"
                                        name={fieldName}
                                        placeholder={item.placeholder || ""}
                                        value={formData[fieldName] ?? ""}
                                        onChange={handleChange}
                                    />
                                )}

                                {isMonthInput && (
                                    <Input
                                        type="month"
                                        name={fieldName}
                                        placeholder={item.placeholder || "YYYY-MM"}
                                        value={formData[fieldName] ?? ""}
                                        onChange={handleChange}
                                    />
                                )}

                                {item.type === "date" && !isMonthInput && (
                                    <Input
                                        type="date"
                                        name={fieldName}
                                        placeholder={item.placeholder || ""}
                                        value={formData[fieldName] ?? ""}
                                        onChange={handleChange}
                                    />
                                )}

                                {item.type === "select" && (
                                    <Select
                                        options={item.itemArray || []}
                                        className="select2-selection"
                                        placeholder={
                                            item.placeholder ||
                                            item.title ||
                                            "Select"
                                        }
                                        isClearable
                                        value={getSelectedOption(
                                            item.itemArray,
                                            formData[fieldName]
                                        )}
                                        onChange={(selectedOption) => {
                                            const nextValue =
                                                selectedOption?.value ?? "";
                                            setFormData((prev) => ({
                                                ...prev,
                                                [fieldName]: nextValue,
                                            }));
                                            if (typeof item.onSelectChange === "function") {
                                                item.onSelectChange(
                                                    selectedOption,
                                                    setFormData,
                                                    fieldName
                                                );
                                            }
                                        }}
                                        menuPortalTarget={
                                            typeof document !== "undefined"
                                                ? document.body
                                                : null
                                        }
                                        menuPosition="fixed"
                                        styles={selectZIndexStyles}
                                    />
                                )}

                                {item.type === "dropdown" && (
                                    <Select
                                        options={dropdownOptionsByField[fieldName] || []}
                                        className="select2-selection"
                                        placeholder={
                                            item.placeholder ||
                                            item.title ||
                                            "Select"
                                        }
                                        isClearable
                                        value={getSelectedOption(
                                            dropdownOptionsByField[fieldName] || [],
                                            formData[fieldName]
                                        )}
                                        onChange={(selectedOption) => {
                                            const nextValue =
                                                selectedOption?.value ?? "";
                                            setFormData((prev) => ({
                                                ...prev,
                                                [fieldName]: nextValue,
                                            }));
                                            if (typeof item.onSelectChange === "function") {
                                                item.onSelectChange(
                                                    selectedOption,
                                                    setFormData,
                                                    fieldName
                                                );
                                            }
                                        }}
                                        menuPortalTarget={
                                            typeof document !== "undefined"
                                                ? document.body
                                                : null
                                        }
                                        menuPosition="fixed"
                                        styles={selectZIndexStyles}
                                    />
                                )}

                                {item.type === "asyncSelect" && resolveAsyncSelectKind(item) === "client" && (
                                    <AsyncClientSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        pageName={pageName}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select Client"}
                                    />
                                )}

                                {item.type === "asyncSelect" && resolveAsyncSelectKind(item) === "source" && (
                                    <AsyncSourceSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select Source"}
                                    />
                                )}

                                {item.type === "asyncSelect" && resolveAsyncSelectKind(item) === "user" && (
                                    <AsyncUserSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        pageName={pageName}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select User"}
                                    />
                                )}

                                {item.type === "autoComplete" && resolveAsyncSelectKind(item) === "client" && (
                                    <AsyncClientSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        pageName={pageName}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select Client"}
                                    />
                                )}

                                {item.type === "autoComplete" && resolveAsyncSelectKind(item) === "source" && (
                                    <AsyncSourceSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select Source"}
                                    />
                                )}

                                {item.type === "autoComplete" && resolveAsyncSelectKind(item) === "user" && (
                                    <AsyncUserSelect
                                        key={`${fieldName}-${String(formData[fieldName] ?? "")}`}
                                        pageName={pageName}
                                        value={formData[fieldName] ?? ""}
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder={item.placeholder || item.title || "Select User"}
                                    />
                                )}

                                {item.type === "autoComplete" && resolveAsyncSelectKind(item) === "candidate" && (
                                    <AsyncCandidateSelect
                                        key={`${fieldName}-${String(formData.clientId ?? "")}-${String(formData[fieldName] ?? "")}`}
                                        value={formData[fieldName] ?? ""}
                                        clientId={formData.clientId ?? ""}
                                        minInputLength={0}
                                        emptyHint="Type to search candidates"
                                        initialOption={
                                            item.initialOption ??
                                            (formData[fieldName] &&
                                            formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                ? {
                                                      value: String(formData[fieldName]),
                                                      label: String(
                                                          formData[getAsyncFilterLabelFieldKey(fieldName)]
                                                      ),
                                                  }
                                                : null)
                                        }
                                        onChange={(selectedOption) =>
                                            handleAsyncSelectChange(fieldName, selectedOption, item)
                                        }
                                        placeholder="Filter Candidate"
                                    />
                                )}

                                {item.type === "multiselect" && (
                                    <Select
                                        isMulti
                                        className="select2-selection"
                                        options={
                                            dropdownOptionsByField[fieldName] ||
                                            item.itemArray ||
                                            []
                                        }
                                        value={(
                                            dropdownOptionsByField[fieldName] ||
                                            item.itemArray ||
                                            []
                                        ).filter((opt) =>
                                            (formData[fieldName] || []).includes(opt.value)
                                        )}
                                        onChange={(selected) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                [fieldName]: (selected || []).map((opt) => opt.value),
                                            }))
                                        }
                                        placeholder={item.placeholder || "Select"}
                                        closeMenuOnSelect={false}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        menuPosition="fixed"
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            menu: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                    />
                                )}

                                {item.type === "radio" && (
                                    <div className="input-box d-flex align-items-center justify-content-between">
                                        <span className="text-muted">{item.placeholder}</span>
                                        <div>
                                            <Input
                                                type="radio"
                                                name={fieldName}
                                                value="on"
                                                checked={formData[fieldName] === "on"}
                                                onChange={handleChange}
                                            />{" "}
                                            On
                                            <Input
                                                type="radio"
                                                name={fieldName}
                                                value="off"
                                                checked={formData[fieldName] === "off"}
                                                onChange={handleChange}
                                                className="ms-3"
                                            />{" "}
                                            Off
                                        </div>
                                    </div>
                                )}

                                {item.type === "toggle" && (
                                    <div className="input-box d-flex align-items-center justify-content-between">
                                        <span className="text-muted">{item.placeholder}</span>
                                        <div className="d-flex align-items-center">
                                            <strong className="me-2">
                                                {formData[fieldName] ? "Active" : "Inactive"}
                                            </strong>
                                            <div className="form-switch">
                                                <Input
                                                    type="switch"
                                                    name={fieldName}
                                                    checked={Boolean(formData[fieldName])}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                </div>
                            </FormGroup>
                        );
                    })}

                    {shouldCustomizeToolbar ? (
                        <ManageFilterToolbarPanel
                            key={`filter-toolbar-prefs-${commonFilterModalOpen ? "open" : "closed"}`}
                            metadataFilterDefinitions={metadataFilterDefinitions}
                            toolbarFieldOrder={toolbarFieldOrder}
                            advancedFieldOrder={advancedFieldOrder}
                            onDragEnd={handleFilterDragEnd}
                            onReset={resetFilterPreferences}
                        />
                    ) : null}

                    <div
                        className="text-end"
                        style={{
                            position: "fixed",
                            bottom: "20px",
                            right: "20px",
                            zIndex: 1050,
                        }}
                    >
                        <Button color="primary" type="submit">
                            Save
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default AllCommonFilterModal;
